import React, { useState } from "react";
import { _axios } from "../../utils/axios";
import { uploadFileToBlob } from "../../utils/azure-storage-blob";
import { ErrorToast, SuccessToast } from "../toastMessage";

interface PermanentRLUploadModalProps {
  applicationId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingKPratUrl?: string;
  existingRegistryCertUrl?: string;
}

export const PermanentRLUploadModal: React.FC<PermanentRLUploadModalProps> = ({
  applicationId,
  isOpen,
  onClose,
  onSuccess,
  existingKPratUrl,
  existingRegistryCertUrl,
}) => {
  const [kPratFile, setKPratFile] = useState<File | null>(null);
  const [registryCertFile, setRegistryCertFile] = useState<File | null>(null);
  const [localKPratUrl, setLocalKPratUrl] = useState<string | undefined>(existingKPratUrl);
  const [localRegistryCertUrl, setLocalRegistryCertUrl] = useState<string | undefined>(existingRegistryCertUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatusText, setUploadStatusText] = useState("");

  React.useEffect(() => {
    setLocalKPratUrl(existingKPratUrl);
  }, [existingKPratUrl]);

  React.useEffect(() => {
    setLocalRegistryCertUrl(existingRegistryCertUrl);
  }, [existingRegistryCertUrl]);

  if (!isOpen) return null;

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleAction = async (isSubmit: boolean) => {
    // If final submission, ensure both files exist (newly selected, locally saved in draft, or existing from server)
    const hasKPrat = Boolean(kPratFile || localKPratUrl || existingKPratUrl);
    const hasRegistry = Boolean(registryCertFile || localRegistryCertUrl || existingRegistryCertUrl);

    if (isSubmit && (!hasKPrat || !hasRegistry)) {
      ErrorToast("Please upload both K Prat and Registry Certificate documents for final submission!");
      return;
    }

    if (!isSubmit && !kPratFile && !registryCertFile) {
      ErrorToast("Please select at least one document to save draft!");
      return;
    }

    try {
      setIsUploading(true);
      let uploadedKPratUrl = "";
      let uploadedRegistryCertUrl = "";

      if (kPratFile) {
        setUploadStatusText("Uploading K Prat document...");
        uploadedKPratUrl = await uploadFileToBlob(
          kPratFile,
          kPratFile.name,
          "permanent_rl",
          applicationId
        );
      }

      if (registryCertFile) {
        setUploadStatusText("Uploading Registry Certificate...");
        uploadedRegistryCertUrl = await uploadFileToBlob(
          registryCertFile,
          registryCertFile.name,
          "permanent_rl",
          applicationId
        );
      }

      setUploadStatusText(isSubmit ? "Submitting Application..." : "Saving Draft...");
      const response = await _axios.post("/applications/submit-permanent-rl", {
        id: applicationId,
        kPratUrl: uploadedKPratUrl || localKPratUrl || undefined,
        registryCertUrl: uploadedRegistryCertUrl || localRegistryCertUrl || undefined,
        isSubmit,
      });

      if (response.data?.success) {
        if (isSubmit) {
          SuccessToast("Permanent RL application submitted successfully!");
          onSuccess();
          onClose();
        } else {
          SuccessToast("Draft saved successfully!");
          if (uploadedKPratUrl) setLocalKPratUrl(uploadedKPratUrl);
          if (uploadedRegistryCertUrl) setLocalRegistryCertUrl(uploadedRegistryCertUrl);
          setKPratFile(null);
          setRegistryCertFile(null);
        }
      } else {
        ErrorToast(response.data?.message || "Failed to process request.");
      }
    } catch (err: any) {
      console.error(err);
      ErrorToast(err?.response?.data?.message || "An error occurred while uploading documents.");
    } finally {
      setIsUploading(false);
      setUploadStatusText("");
    }
  };

  const getFileNameFromUrl = (url?: string) => {
    if (!url) return "";
    try {
      const cleanUrl = url.split("?")[0];
      const fileName = cleanUrl.split("/").pop() || "";
      return decodeURIComponent(fileName);
    } catch (e) {
      return "Saved Document";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-xl shadow-2xl border border-gray-200">
        {/* Modal Header */}
        <div className="flex justify-between items-center pb-4 mb-5 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Apply for Permanent RL</h3>
            <p className="text-xs text-gray-500 mt-1">
              Upload required K Prat & Registry documents to initiate Phase 2 review.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="text-gray-400 hover:text-gray-600 font-bold text-2xl w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition"
          >
            &times;
          </button>
        </div>

        {/* Modal Content */}
        <div className="space-y-6">
          {/* File 1: K Prat Document */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-bold text-gray-800">
                1. K Prat Document <span className="text-red-500">*</span>
              </label>
              {localKPratUrl && (
                <a
                  href={localKPratUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                >
                  <i className="fa fa-eye"></i> View Existing
                </a>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label className="cursor-pointer bg-primary text-white text-xs font-bold px-4 py-2 rounded-full shadow-sm hover:opacity-90 transition flex items-center gap-2 shrink-0">
                <i className="fa fa-upload"></i>
                {localKPratUrl || kPratFile ? "Change File" : "Choose K Prat File"}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setKPratFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
              <div className="flex-1 min-w-0">
                {kPratFile ? (
                  <div className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-gray-300">
                    <span className="text-xs font-semibold text-gray-800 truncate mr-2">
                      📄 New: {kPratFile.name}
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0 font-medium">
                      {formatFileSize(kPratFile.size)}
                    </span>
                  </div>
                ) : localKPratUrl ? (
                  <div className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-green-300">
                    <span className="text-xs font-semibold text-gray-800 truncate mr-2">
                      📄 {getFileNameFromUrl(localKPratUrl)}
                    </span>
                    <span className="text-[10px] font-bold text-green-700 shrink-0">
                      ✓ Saved in Draft
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 italic">No file selected (.pdf, .jpg, .png)</span>
                )}
              </div>
            </div>
          </div>

          {/* File 2: Registry Certificate */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-bold text-gray-800">
                2. Registry Certificate <span className="text-red-500">*</span>
              </label>
              {localRegistryCertUrl && (
                <a
                  href={localRegistryCertUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                >
                  <i className="fa fa-eye"></i> View Existing
                </a>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label className="cursor-pointer bg-primary text-white text-xs font-bold px-4 py-2 rounded-full shadow-sm hover:opacity-90 transition flex items-center gap-2 shrink-0">
                <i className="fa fa-upload"></i>
                {localRegistryCertUrl || registryCertFile ? "Change File" : "Choose Registry File"}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setRegistryCertFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
              <div className="flex-1 min-w-0">
                {registryCertFile ? (
                  <div className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-gray-300">
                    <span className="text-xs font-semibold text-gray-800 truncate mr-2">
                      📄 New: {registryCertFile.name}
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0 font-medium">
                      {formatFileSize(registryCertFile.size)}
                    </span>
                  </div>
                ) : localRegistryCertUrl ? (
                  <div className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-green-300">
                    <span className="text-xs font-semibold text-gray-800 truncate mr-2">
                      📄 {getFileNameFromUrl(localRegistryCertUrl)}
                    </span>
                    <span className="text-[10px] font-bold text-green-700 shrink-0">
                      ✓ Saved in Draft
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 italic">No file selected (.pdf, .jpg, .png)</span>
                )}
              </div>
            </div>
          </div>

          {/* Upload Status Banner */}
          {isUploading && (
            <div className="bg-gray-100 border border-gray-300 p-3 rounded-lg flex items-center gap-3 text-gray-800 text-xs font-semibold">
              <i className="fa fa-spinner fa-spin text-primary text-sm"></i>
              {uploadStatusText}
            </div>
          )}

          {/* Modal Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="bg-black px-6 py-2 rounded-full font-bold text-white text-xs disabled:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleAction(false)}
              disabled={isUploading || (!kPratFile && !registryCertFile)}
              className="bg-gray-700 text-white px-6 py-2 rounded-full font-bold text-xs hover:bg-gray-800 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Save Draft
            </button>
            <button
              type="button"
              onClick={() => handleAction(true)}
              disabled={
                isUploading ||
                (!kPratFile && !localKPratUrl) ||
                (!registryCertFile && !localRegistryCertUrl)
              }
              className="bg-primary px-6 py-2 rounded-full font-bold text-white text-xs hover:opacity-90 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isUploading ? "Uploading..." : "Submit for Permanent RL"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
